import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { 
  User, Briefcase, MessageSquare, FileText, Settings, Bell,
  ChevronRight, Clock, CheckCircle, XCircle, Eye, Send,
  Calendar, MapPin, Euro, Building2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import RetraiteProfileEdit from '@/components/profile/RetraiteProfileEdit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get stored auth data
const getAuthData = () => {
  try {
    const token = localStorage.getItem('sfretire_token');
    const user = JSON.parse(localStorage.getItem('sfretire_user') || 'null');
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    approved: { label: 'Approuvé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    rejected: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: XCircle },
    sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-800', icon: Send },
    viewed: { label: 'Consultée', color: 'bg-purple-100 text-purple-800', icon: Eye },
    shortlisted: { label: 'Présélectionné', color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
    accepted: { label: 'Acceptée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Application card component
const ApplicationCard = ({ application, onViewDetails }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-medium text-foreground line-clamp-1">
            {application.mission_title || 'Mission'}
          </h4>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
            <Building2 size={14} />
            {application.entreprise_name || 'Entreprise'}
          </p>
        </div>
        <StatusBadge status={application.status} />
      </div>
      
      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
        <span className="flex items-center gap-1">
          <Calendar size={12} />
          {new Date(application.created_at).toLocaleDateString('fr-FR')}
        </span>
        {application.proposed_rate && (
          <span className="flex items-center gap-1">
            <Euro size={12} />
            {application.proposed_rate}€/jour
          </span>
        )}
      </div>

      {application.cover_letter && (
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {application.cover_letter}
        </p>
      )}

      <Button 
        variant="ghost" 
        size="sm" 
        className="w-full justify-between"
        onClick={() => onViewDetails(application)}
      >
        Voir les détails
        <ChevronRight size={16} />
      </Button>
    </motion.div>
  );
};

// Conversation card component
const ConversationCard = ({ conversation, onClick }) => {
  const hasUnread = conversation.unread_retraite > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`bg-card border rounded-xl p-4 cursor-pointer hover:shadow-md transition-all ${
        hasUnread ? 'border-primary/50 bg-primary/5' : 'border-border'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Building2 size={20} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-foreground truncate">
              {conversation.other_party?.name || 'Contact'}
            </h4>
            {hasUnread && (
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                {conversation.unread_retraite}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate mt-1">
            {conversation.last_message_preview || 'Aucun message'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {conversation.last_message_at 
              ? new Date(conversation.last_message_at).toLocaleDateString('fr-FR')
              : ''
            }
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Main Dashboard Component
const RetraiteDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    unreadMessages: 0
  });

  const token = localStorage.getItem('sfretire_token');

  useEffect(() => {
    const storedUser = localStorage.getItem('sfretire_user');
    let user = null;
    try { user = JSON.parse(storedUser); } catch {}

    if (!token || !user) {
      navigate('/');
      return;
    }

    if (user.role !== 'retraite' && user.user_type !== 'retraite') {
      navigate('/dashboard/entreprise');
      return;
    }

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch user profile
      const meRes = await fetch(`${API_URL}/api/auth/me`, { headers });
      if (meRes.ok) {
        const meData = await meRes.json();
        setProfile(meData.user?.profile);
      }

      // Fetch applications
      const appsRes = await fetch(`${API_URL}/api/applications`, { headers });
      if (appsRes.ok) {
        const appsData = await appsRes.json();
        setApplications(appsData.items || []);
        setStats(prev => ({
          ...prev,
          totalApplications: appsData.total || 0,
          pendingApplications: (appsData.items || []).filter(a => a.status === 'sent' || a.status === 'viewed').length
        }));
      }

      // Fetch conversations
      const convRes = await fetch(`${API_URL}/api/conversations`, { headers });
      if (convRes.ok) {
        const convData = await convRes.json();
        setConversations(convData.items || []);
        const unread = (convData.items || []).reduce((sum, c) => sum + (c.unread_retraite || 0), 0);
        setStats(prev => ({ ...prev, unreadMessages: unread }));
      }

    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: User },
    { id: 'applications', label: 'Candidatures', icon: FileText },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'profile', label: 'Mon profil', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-serif font-bold text-foreground">
              Bonjour, {profile?.first_name || 'Expert'} !
            </h1>
            <p className="text-muted-foreground mt-2">
              Gérez vos candidatures et vos conversations
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="text-primary" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalApplications}</p>
                  <p className="text-sm text-muted-foreground">Candidatures totales</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="text-yellow-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.pendingApplications}</p>
                  <p className="text-sm text-muted-foreground">En attente</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <MessageSquare className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.unreadMessages}</p>
                  <p className="text-sm text-muted-foreground">Messages non lus</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-border mb-6 overflow-x-auto pb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Recent Applications */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Candidatures récentes</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('applications')}>
                    Voir tout <ChevronRight size={16} />
                  </Button>
                </div>
                <div className="space-y-3">
                  {applications.slice(0, 3).map(app => (
                    <ApplicationCard 
                      key={app.id} 
                      application={app}
                      onViewDetails={() => {}}
                    />
                  ))}
                  {applications.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="mx-auto mb-2 opacity-50" size={32} />
                      <p>Aucune candidature pour le moment</p>
                      <Button variant="outline" size="sm" className="mt-3" asChild>
                        <Link to="/missions">Voir les missions</Link>
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Conversations */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Conversations</h2>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('messages')}>
                    Voir tout <ChevronRight size={16} />
                  </Button>
                </div>
                <div className="space-y-3">
                  {conversations.slice(0, 3).map(conv => (
                    <ConversationCard 
                      key={conv.id} 
                      conversation={conv}
                      onClick={() => {}}
                    />
                  ))}
                  {conversations.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="mx-auto mb-2 opacity-50" size={32} />
                      <p>Aucune conversation pour le moment</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'applications' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-foreground">
                  Toutes mes candidatures ({applications.length})
                </h2>
                <Button variant="gold" size="sm" asChild>
                  <Link to="/missions">Voir les missions</Link>
                </Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {applications.map(app => (
                  <ApplicationCard 
                    key={app.id} 
                    application={app}
                    onViewDetails={() => {}}
                  />
                ))}
              </div>
              {applications.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <FileText className="mx-auto mb-4 opacity-50" size={48} />
                  <p className="text-lg">Vous n&apos;avez pas encore postulé</p>
                  <p className="text-sm mt-2">Parcourez les missions disponibles et postulez !</p>
                  <Button variant="gold" className="mt-4" asChild>
                    <Link to="/missions">Découvrir les missions</Link>
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-6">
                Mes conversations ({conversations.length})
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {conversations.map(conv => (
                  <ConversationCard 
                    key={conv.id} 
                    conversation={conv}
                    onClick={() => {}}
                  />
                ))}
              </div>
              {conversations.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <MessageSquare className="mx-auto mb-4 opacity-50" size={48} />
                  <p className="text-lg">Pas de conversation pour le moment</p>
                  <p className="text-sm mt-2">Les entreprises vous contacteront après vos candidatures</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="max-w-2xl">
              <h2 className="text-lg font-semibold text-foreground mb-6">Mon profil</h2>
              
              {/* Profile Status */}
              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">Statut du profil</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {profile?.validation_status === 'approved' ? 'Votre profil est approuvé et visible' : 'Votre profil est en cours de validation'}
                    </p>
                  </div>
                  <StatusBadge status={profile?.validation_status || 'pending'} />
                </div>
              </div>

              {editing ? (
                <RetraiteProfileEdit
                  profile={profile}
                  token={token}
                  onSaved={(updated) => {
                    setProfile(updated);
                    setEditing(false);
                  }}
                  onCancel={() => setEditing(false)}
                />
              ) : (
                <>
                  {/* Profile Details */}
                  <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                    <div className="flex items-center gap-4">
                      {profile?.photo_url ? (
                        <img src={profile.photo_url} alt="" className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                          <User size={28} className="text-primary" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-semibold text-foreground">
                          {profile?.first_name} {profile?.last_name}
                        </h3>
                        <p className="text-muted-foreground">{profile?.title || 'Expert'}</p>
                      </div>
                    </div>

                    {profile?.bio && (
                      <div className="pt-4 border-t border-border">
                        <p className="text-sm text-muted-foreground mb-1">Bio</p>
                        <p className="text-sm text-foreground">{profile.bio}</p>
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-border">
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium">{profile?.email || 'Non renseigné'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Téléphone</p>
                        <p className="font-medium">{profile?.phone || 'Non renseigné'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Localisation</p>
                        <p className="font-medium">{profile?.location || 'Non renseignée'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Tarif horaire</p>
                        <p className="font-medium">{profile?.hourly_rate ? `${profile.hourly_rate}EUR/h` : 'Non renseigné'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Expérience</p>
                        <p className="font-medium">{profile?.experience_years ? `${profile.experience_years} ans` : 'Non renseigné'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Disponibilité</p>
                        <p className="font-medium capitalize">{profile?.availability || 'Flexible'}</p>
                      </div>
                    </div>

                    {profile?.skills && profile.skills.length > 0 && (
                      <div className="pt-4 border-t border-border">
                        <p className="text-sm text-muted-foreground mb-2">Compétences</p>
                        <div className="flex flex-wrap gap-2">
                          {profile.skills.map((skill, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button variant="outline" className="w-full mt-4" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                      <Settings size={16} className="mr-2" />
                      Modifier mon profil
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RetraiteDashboard;
